import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  REPLACEMENT_KIND_LABELS,
  REPLACEMENT_STATUS_LABELS,
  roleCanManageReplacements,
  type PublicReplacementRequest,
  type ReplacementListResponse,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

export function ReplacementsPage(): JSX.Element {
  const { user } = useAuth();
  const canManage = user ? roleCanManageReplacements(user.role) : false;
  const [rows, setRows] = useState<PublicReplacementRequest[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<ReplacementListResponse>('/api/replacements?open=true');
        if (!cancelled) setRows(body.replacements);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError
              ? caught.message
              : 'Could not load replacement requests.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section data-testid="replacements-page">
      <div className="card">
        <h2>Replacement requests</h2>
        <p className="meta">Internal tickets for missing and broken gear. Not a vendor RMA.</p>
        {error ? (
          <p className="status status--error" data-testid="replacements-error">
            {error}
          </p>
        ) : null}
        {canManage ? (
          <Link className="button" to="/replacements/new" data-testid="new-replacement">
            New replacement request
          </Link>
        ) : (
          <p className="meta">Only an admin or equipment admin can open or resolve tickets.</p>
        )}
      </div>
      {rows.length === 0 && !error ? <p className="meta">No open replacement requests.</p> : null}
      {rows.map((row) => (
        <article className="card" key={row.id} data-testid={`replacement-row-${row.id}`}>
          <h2>
            <Link to={`/replacements/${row.id}`}>
              {REPLACEMENT_KIND_LABELS[row.kind]} ·{' '}
              {row.asset?.serial ?? row.tote?.number ?? row.id}
            </Link>
          </h2>
          <p className="meta">
            {REPLACEMENT_STATUS_LABELS[row.status]}
            {row.event ? ` · ${row.event.name}` : ''}
            {row.tote ? ` · tote ${row.tote.number}` : ''}
          </p>
          <p>{row.description}</p>
        </article>
      ))}
    </section>
  );
}
